import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { useAuth } from './contexts/AuthContext';

import NotFound from "pages/NotFound";
import UserLogin from './pages/user-login';
import JobSeekerDashboard from './pages/job-seeker-dashboard';
import JobDetails from './pages/job-details';
import UserProfileSettings from './pages/user-profile-settings';
import PostNewJob from './pages/post-new-job';
import CompanyProfile from './pages/company-profile';
import JobListings from './pages/job-listings';
import EmployerDashboard from './pages/employer-dashboard';
import UserRegistration from './pages/user-registration';
import JobApplication from './pages/job-application';
import ApplicationTracking from './pages/application-tracking';
import CandidateManagement from './pages/candidate-management';

// Role-based protected route component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, userProfile, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/user-login" replace />;
  }

  if (requiredRole && userProfile?.role !== requiredRole) {
    // Redirect to appropriate dashboard based on their role
    const redirectPath = userProfile?.role === 'employer' ?'/employer-dashboard' :'/job-seeker-dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes */}
          <Route path="/user-login" element={<UserLogin />} />
          <Route path="/user-registration" element={<UserRegistration />} />

          {/* Job Seeker routes */}
          <Route 
            path="/job-seeker-dashboard" 
            element={
              <ProtectedRoute requiredRole="jobseeker">
                <JobSeekerDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/job-listings" 
            element={
              <ProtectedRoute requiredRole="jobseeker">
                <JobListings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/job-details" 
            element={
              <ProtectedRoute requiredRole="jobseeker">
                <JobDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/job-application" 
            element={
              <ProtectedRoute requiredRole="jobseeker">
                <JobApplication />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/application-tracking" 
            element={
              <ProtectedRoute requiredRole="jobseeker">
                <ApplicationTracking />
              </ProtectedRoute>
            } 
          />

          {/* Employer routes */}
          <Route 
            path="/employer-dashboard" 
            element={
              <ProtectedRoute requiredRole="employer">
                <EmployerDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/post-new-job" 
            element={
              <ProtectedRoute requiredRole="employer">
                <PostNewJob />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/candidate-management" 
            element={
              <ProtectedRoute requiredRole="employer">
                <CandidateManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/company-profile" 
            element={
              <ProtectedRoute requiredRole="employer">
                <CompanyProfile />
              </ProtectedRoute>
            } 
          />

          {/* Shared routes (both roles) */}
          <Route 
            path="/user-profile-settings" 
            element={
              <ProtectedRoute>
                <UserProfileSettings />
              </ProtectedRoute>
            } 
          />

          {/* Default redirect based on authentication */}
          <Route 
            path="/" 
            element={
              <Navigate to="/user-login" replace />
            } 
          />

          {/* 404 page */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;